import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

type Props = {
  activeKey?: string
  offset?: number
}

export const useScrollOverflow = ({ activeKey, offset }: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const isMounted = useRef(false)
  const [leftOverflow, setLeftOverflow] = useState(false)
  const [rightOverflow, setRightOverflow] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollEndTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    setLeftOverflow(el.scrollLeft > 0)
    setRightOverflow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  const onScroll = useCallback(() => {
    measure()
    setIsScrolling(true)
    if (scrollEndTimeout.current) clearTimeout(scrollEndTimeout.current)
    scrollEndTimeout.current = setTimeout(() => setIsScrolling(false), 500)
  }, [measure])

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = ref.current
    if (!el) return
    el.scrollBy({
      behavior: 'smooth',
      left: direction === 'left' ? -el.clientWidth : el.clientWidth,
    })
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.addEventListener('scroll', onScroll, { passive: true })

    let frame = 0
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }

    const resizeObserver = new ResizeObserver(scheduleMeasure)
    resizeObserver.observe(el)

    const mutationObserver = new MutationObserver(scheduleMeasure)
    mutationObserver.observe(el, { characterData: true, childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('scroll', onScroll)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      if (scrollEndTimeout.current) clearTimeout(scrollEndTimeout.current)
    }
  }, [measure, onScroll])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const active = el.querySelector<HTMLElement>('[data-scroll-active]')
    if (!active) return
    el.scrollTo({ behavior: isMounted.current ? 'smooth' : 'instant', left: active.offsetLeft - (offset || 0) })
  }, [activeKey, offset])

  // Instantly scrollTo before mount, otherwise smoothly scroll
  useLayoutEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  return { isScrolling, leftOverflow, ref, rightOverflow, scroll }
}
